import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/lib/api';
import { CircleNotch, MapPin, MagnifyingGlass, Crosshair, Globe, ArrowsClockwise, Pause } from '@phosphor-icons/react';

// City coordinates lookup (lon, lat)
const cityCoords = {
  'berlin': [13.405, 52.52], 'münchen': [11.582, 48.1351], 'munich': [11.582, 48.1351], 'hamburg': [9.9937, 53.5511],
  'köln': [6.9603, 50.9375], 'frankfurt': [8.6821, 50.1109], 'stuttgart': [9.1829, 48.7758], 'düsseldorf': [6.7735, 51.2277],
  'dortmund': [7.4653, 51.5136], 'essen': [7.0116, 51.4556], 'leipzig': [12.3731, 51.3397], 'bremen': [8.8017, 53.0793],
  'dresden': [13.7373, 51.0504], 'hannover': [9.732, 52.3759], 'nürnberg': [11.0767, 49.4521], 'duisburg': [6.7623, 51.4344],
  'bochum': [7.2162, 51.4818], 'wuppertal': [7.1508, 51.2562], 'bielefeld': [8.5325, 52.0302], 'bonn': [7.0982, 50.7374],
  'mannheim': [8.466, 49.4875], 'karlsruhe': [8.4037, 49.0069], 'augsburg': [10.8978, 48.3705], 'wiesbaden': [8.2398, 50.0782],
  'schönefeld': [13.5225, 52.38], 'schoenefeld': [13.5225, 52.38], 'potsdam': [13.0645, 52.3906],
  'wien': [16.3738, 48.2082], 'zürich': [8.5417, 47.3769], 'paris': [2.3522, 48.8566], 'london': [-0.1278, 51.5074],
  'amsterdam': [4.9041, 52.3676], 'brüssel': [4.3517, 50.8503], 'rom': [12.4964, 41.9028], 'madrid': [-3.7038, 40.4168],
  'prag': [14.4378, 50.0755], 'warschau': [21.0122, 52.2297], 'budapest': [19.0402, 47.4979],
  'istanbul': [28.9784, 41.0082], 'ankara': [32.8597, 39.9334], 'izmir': [27.1428, 38.4237],
  'antalya': [30.7133, 36.8969], 'bursa': [29.0610, 40.1885], 'adana': [35.3213, 36.9914],
  'new york': [-74.006, 40.7128], 'los angeles': [-118.2437, 34.0522], 'chicago': [-87.6298, 41.8781],
  'tokyo': [139.6917, 35.6895], 'peking': [116.4074, 39.9042], 'beijing': [116.4074, 39.9042],
  'dubai': [55.2708, 25.2048], 'singapur': [103.8198, 1.3521], 'singapore': [103.8198, 1.3521],
  'sydney': [151.2093, -33.8688], 'melbourne': [144.9631, -37.8136],
};

const getCoordsForCity = (city) => {
  if (!city) return null;
  const key = city.toLowerCase().trim();
  if (cityCoords[key]) return cityCoords[key];
  for (const [k, v] of Object.entries(cityCoords)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return null;
};

// Load CesiumJS via CDN
const loadCesium = () => {
  return new Promise((resolve) => {
    if (window.Cesium) { resolve(window.Cesium); return; }
    if (!document.getElementById('cesium-css')) {
      const link = document.createElement('link');
      link.id = 'cesium-css';
      link.rel = 'stylesheet';
      link.href = 'https://cesium.com/downloads/cesiumjs/releases/1.119/Build/Cesium/Widgets/widgets.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://cesium.com/downloads/cesiumjs/releases/1.119/Build/Cesium/Cesium.js';
    script.onload = () => {
      window.CESIUM_BASE_URL = 'https://cesium.com/downloads/cesiumjs/releases/1.119/Build/Cesium/';
      resolve(window.Cesium);
    };
    document.head.appendChild(script);
  });
};

export default function MapPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [flyingTo, setFlyingTo] = useState(null);
  const [cesiumReady, setCesiumReady] = useState(false);
  const [orbiting, setOrbiting] = useState(false);
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const entitiesRef = useRef({});
  const orbitRef = useRef(null);
  const idleTimerRef = useRef(null);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await api.get('/map/devices');
        setDevices(res.data.devices || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchDevices();
  }, []);

  // Orbit mode functions
  const startOrbit = useCallback(() => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer || orbitRef.current) return;
    setOrbiting(true);
    const onTick = () => {
      if (viewer.isDestroyed()) return;
      viewer.camera.rotateRight(0.001);
    };
    viewer.clock.onTick.addEventListener(onTick);
    orbitRef.current = onTick;
  }, []);

  const stopOrbit = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer || !orbitRef.current) return;
    viewer.clock.onTick.removeEventListener(orbitRef.current);
    orbitRef.current = null;
    setOrbiting(false);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    stopOrbit();
    idleTimerRef.current = setTimeout(() => startOrbit(), 30000); // 30s idle → orbit
  }, [startOrbit, stopOrbit]);

  // Initialize Cesium globe
  useEffect(() => {
    if (loading || !containerRef.current) return;
    let viewer = null;

    loadCesium().then((Cesium) => {
      if (viewerRef.current) return;
      Cesium.Ion.defaultAccessToken = undefined;

      viewer = new Cesium.Viewer(containerRef.current, {
        baseLayer: false,
        baseLayerPicker: false,
        geocoder: false,
        homeButton: false,
        sceneModePicker: false,
        selectionIndicator: false,
        timeline: false,
        animation: false,
        fullscreenButton: false,
        vrButton: false,
        navigationHelpButton: false,
        infoBox: false,
        creditContainer: document.createElement('div'),
        skyBox: false,
        contextOptions: { webgl: { alpha: true } }
      });

      // Google Satellite Hybrid Tiles (satellite + labels, max zoom 21)
      const googleHybrid = new Cesium.UrlTemplateImageryProvider({
        url: 'https://mt{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
        subdomains: ['0', '1', '2', '3'],
        maximumLevel: 21,
        credit: 'Google',
      });
      viewer.imageryLayers.addImageryProvider(googleHybrid);

      // Dark atmosphere & styling
      try {
        if (viewer.scene.skyAtmosphere) {
          viewer.scene.skyAtmosphere.hueShift = -0.05;
          viewer.scene.skyAtmosphere.saturationShift = -0.2;
          viewer.scene.skyAtmosphere.brightnessShift = -0.3;
        }
        viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#0a0a0b');
        viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString('#111');
        viewer.scene.globe.enableLighting = false;
        viewer.scene.fog.enabled = true;
        viewer.scene.fog.density = 0.0002;
        viewer.scene.globe.showGroundAtmosphere = true;
        if (viewer.scene.sun) viewer.scene.sun.show = false;
        if (viewer.scene.moon) viewer.scene.moon.show = false;
      } catch (e) { console.warn('Style:', e); }

      // Device markers
      const bounds = [];
      devices.forEach(d => {
        const coords = getCoordsForCity(d.location_city);
        if (!coords) return;
        const [lon, lat] = coords;
        const isOnline = d.is_online;

        viewer.entities.add({
          id: d.id,
          name: d.computer_name,
          position: Cesium.Cartesian3.fromDegrees(lon, lat, 30),
          point: {
            pixelSize: 12,
            color: isOnline ? Cesium.Color.fromCssColorString('#22c55e') : Cesium.Color.fromCssColorString('#ef4444'),
            outlineColor: isOnline ? Cesium.Color.fromCssColorString('#15803d') : Cesium.Color.fromCssColorString('#991b1b'),
            outlineWidth: 2,
            heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new Cesium.NearFarScalar(200, 2.0, 500000, 0.5),
          },
          label: {
            text: d.computer_name,
            font: 'bold 13px "Segoe UI", system-ui, sans-serif',
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 4,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -18),
            heightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scaleByDistance: new Cesium.NearFarScalar(200, 1.2, 300000, 0.3),
            distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 400000),
          },
        });

        entitiesRef.current[d.id] = { lon, lat };
        bounds.push(Cesium.Cartesian3.fromDegrees(lon, lat));
      });

      // Double-click → device detail
      const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
      handler.setInputAction((click) => {
        const picked = viewer.scene.pick(click.position);
        if (Cesium.defined(picked) && picked.id) {
          const deviceId = picked.id.id;
          if (deviceId && entitiesRef.current[deviceId]) navigate(`/devices/${deviceId}`);
        }
      }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

      // User interaction resets idle timer
      const resetOnInteraction = () => resetIdleTimer();
      handler.setInputAction(resetOnInteraction, Cesium.ScreenSpaceEventType.LEFT_DOWN);
      handler.setInputAction(resetOnInteraction, Cesium.ScreenSpaceEventType.WHEEL);
      handler.setInputAction(resetOnInteraction, Cesium.ScreenSpaceEventType.RIGHT_DOWN);

      viewerRef.current = viewer;
      setCesiumReady(true);

      // Initial camera: Germany overview
      if (bounds.length > 0) {
        viewer.camera.flyTo({
          destination: Cesium.Cartesian3.fromDegrees(10.45, 51.16, 1200000),
          orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 },
          duration: 2.5,
          easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
        });
      } else {
        viewer.camera.setView({
          destination: Cesium.Cartesian3.fromDegrees(10.45, 51.16, 2000000),
          orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 }
        });
      }

      // Start idle timer for orbit
      idleTimerRef.current = setTimeout(() => startOrbit(), 30000);
    });

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (viewerRef.current && !viewerRef.current.isDestroyed()) {
        if (orbitRef.current) viewerRef.current.clock.onTick.removeEventListener(orbitRef.current);
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      entitiesRef.current = {};
    };
  }, [loading, devices, navigate, startOrbit, resetIdleTimer]);

  // Cinematic 3-phase fly-to
  const cinematicFlyTo = useCallback((deviceId) => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    const target = entitiesRef.current[deviceId];
    if (!Cesium || !viewer || !target) return;

    stopOrbit();
    setFlyingTo(deviceId);
    const { lon, lat } = target;

    // Phase 1: Pull back to regional view
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(lon, lat, 600000),
      orientation: { heading: Cesium.Math.toRadians(20), pitch: Cesium.Math.toRadians(-50), roll: 0 },
      duration: 2.0,
      easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
      complete: () => {
        // Phase 2: Dive to city level
        setTimeout(() => {
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(lon, lat, 20000),
            orientation: { heading: Cesium.Math.toRadians(-10), pitch: Cesium.Math.toRadians(-40), roll: 0 },
            duration: 3.0,
            easingFunction: Cesium.EasingFunction.QUARTIC_IN_OUT,
            complete: () => {
              // Phase 3: Close-up with cinematic rotation - centered on device
              setTimeout(() => {
                viewer.camera.flyTo({
                  destination: Cesium.Cartesian3.fromDegrees(lon + 0.001, lat, 1800),
                  orientation: { heading: Cesium.Math.toRadians(30), pitch: Cesium.Math.toRadians(-30), roll: 0 },
                  duration: 2.5,
                  easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
                  complete: () => {
                    setFlyingTo(null);
                    resetIdleTimer();
                  },
                });
              }, 200);
            }
          });
        }, 300);
      }
    });
  }, [stopOrbit, resetIdleTimer]);

  // Reset view
  const resetView = useCallback(() => {
    const Cesium = window.Cesium;
    const viewer = viewerRef.current;
    if (!Cesium || !viewer) return;
    stopOrbit();
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(10.45, 51.16, 1200000),
      orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 },
      duration: 2.5,
      easingFunction: Cesium.EasingFunction.CUBIC_IN_OUT,
      complete: () => resetIdleTimer(),
    });
  }, [stopOrbit, resetIdleTimer]);

  const toggleOrbit = useCallback(() => {
    if (orbiting) { stopOrbit(); resetIdleTimer(); }
    else startOrbit();
  }, [orbiting, startOrbit, stopOrbit, resetIdleTimer]);

  const filteredDevices = search
    ? devices.filter(d => d.computer_name?.toLowerCase().includes(search.toLowerCase()) || d.location_city?.toLowerCase().includes(search.toLowerCase()))
    : devices;

  const devicesWithLocation = devices.filter(d => getCoordsForCity(d.location_city));

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>;

  return (
    <div data-testid="map-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Globe size={28} weight="duotone" className="text-[#D50C2D]" />
            {t('nav.map') || 'Geräte-Karte'}
          </h1>
          <p className="text-[#a1a1aa] text-sm mt-1">
            {devicesWithLocation.length} von {devices.length} {t('common.devices') || 'Geräte'} auf der Karte
            {flyingTo && <span className="ml-2 text-[#D50C2D] animate-pulse">— Fly-To aktiv...</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleOrbit} title={orbiting ? 'Orbit stoppen' : 'Orbit starten'}
            className={`flex items-center gap-1.5 px-3 py-1.5 border rounded text-xs transition-colors
              ${orbiting ? 'bg-[#D50C2D]/10 border-[#D50C2D]/30 text-[#D50C2D]' : 'bg-white/5 hover:bg-white/10 border-white/10 text-[#a1a1aa] hover:text-white'}`}
            data-testid="map-orbit-toggle">
            {orbiting ? <><Pause size={14} /> Orbit</> : <><ArrowsClockwise size={14} /> Orbit</>}
          </button>
          <button onClick={resetView}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs text-[#a1a1aa] hover:text-white transition-colors"
            data-testid="map-reset-view">
            <Globe size={14} /> Übersicht
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* 3D Globe */}
        <div className="lg:col-span-3 bg-[#0a0a0b] border border-white/10 rounded overflow-hidden relative" style={{ height: '650px' }}>
          {!cesiumReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0b] z-10">
              <div className="text-center">
                <CircleNotch size={40} className="animate-spin text-[#D50C2D] mx-auto mb-3" />
                <p className="text-[#a1a1aa] text-sm">3D-Globus wird geladen...</p>
              </div>
            </div>
          )}
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="cesium-container" />
        </div>

        {/* Sidebar */}
        <div className="bg-[#141416] border border-white/10 rounded overflow-hidden flex flex-col" style={{ maxHeight: '650px' }}>
          <div className="p-3 border-b border-white/5">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525b]" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search') || 'Suche...'}
                className="w-full h-8 pl-8 pr-3 bg-[#0A0A0B] border border-white/10 rounded text-xs text-white placeholder:text-[#52525b] focus:outline-none focus:border-[#D50C2D]"
                data-testid="map-search" />
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {filteredDevices.map(d => {
              const hasCoords = !!getCoordsForCity(d.location_city);
              const isFlying = flyingTo === d.id;
              return (
                <div key={d.id}
                  className={`flex items-center gap-2 px-3 py-2.5 border-b border-white/5 cursor-pointer transition-all group
                    ${isFlying ? 'bg-[#D50C2D]/10 border-l-2 border-l-[#D50C2D]' : 'hover:bg-white/5'}`}
                  data-testid={`map-device-${d.id}`}
                  onClick={() => hasCoords ? cinematicFlyTo(d.id) : null}
                  onDoubleClick={() => navigate(`/devices/${d.id}`)}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${d.is_online ? 'bg-green-400 shadow-[0_0_6px_#4ade8060]' : 'bg-red-400'}`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-white text-xs font-medium block truncate">{d.computer_name}</span>
                    {d.location_city && (
                      <span className="text-[#52525b] text-[10px] flex items-center gap-1">
                        <MapPin size={9} />{[d.location_city, d.location_country].filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                  {hasCoords && (
                    <Crosshair size={14} className={`shrink-0 transition-colors ${isFlying ? 'text-[#D50C2D] animate-pulse' : 'text-transparent group-hover:text-[#52525b]'}`} />
                  )}
                </div>
              );
            })}
          </div>

          <div className="p-2 border-t border-white/5 bg-[#0a0a0b]">
            <div className="flex items-center justify-between text-[10px] text-[#52525b]">
              <span>{devicesWithLocation.length} mit Standort</span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />{devices.filter(d => d.is_online).length} online
              </span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cesium-viewer { background: #0a0a0b !important; }
        .cesium-viewer-bottom { display: none !important; }
        .cesium-widget-credits { display: none !important; }
        .cesium-viewer .cesium-widget { background: transparent !important; }
        .cesium-viewer-toolbar { display: none !important; }
      `}</style>
    </div>
  );
}
